import React, { useEffect, useRef, useState } from 'react';
import { Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import ChatMessage from './ChatMessage';

const TransportChatTab = ({ request }) => {
  const { socket } = useSocket();
  const { user } = useAuth();

  const requestId = request?.id || null;

  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  useEffect(() => {
    if (!socket) return;

    const handleHistory = (history) => {
      setMessages(Array.isArray(history) ? history : []);
      scrollToBottom();
    };

    const handleChatMessage = (data) => {
      setMessages((prev) => [...prev, data]);
      scrollToBottom();
    };

    const handleError = (data) => {
      if (data?.message) toast.error(data.message);
    };

    socket.on('transport:history', handleHistory);
    socket.on('transport:chat_message', handleChatMessage);
    socket.on('transport:error', handleError);

    return () => {
      socket.off('transport:history', handleHistory);
      socket.off('transport:chat_message', handleChatMessage);
      socket.off('transport:error', handleError);
    };
  }, [socket]);

  useEffect(() => {
    setMessages([]);
    setInputMessage('');

    if (!socket || !requestId || !user) return;

    socket.emit('transport:join_chat', { requestId });

    return () => {
      socket.emit('transport:leave_chat', { requestId });
    };
  }, [socket, requestId, user]);

  const handleSendMessage = (e) => {
    e.preventDefault();

    if (!socket || !user || !requestId) return;

    const text = inputMessage.trim();
    if (!text) return;

    socket.emit('transport:chat_message', {
      requestId,
      message: text
    });

    setInputMessage('');
  };

  const fromLabel = String(request?.from_location?.label || request?.from_location?.address || 'Origen');
  const toLabel = String(request?.to_location?.label || request?.to_location?.address || 'Destino');

  const shortId = requestId ? String(requestId).slice(0, 6) : '------';

  return (
    <div className="tab-panel">
      <div className="tab-info room-info-header">
        <span className="tab-title">🚗 Chat Transporte</span>
        <span className="tab-subtitle">
          #{shortId} · {fromLabel} → {toLabel}
        </span>
      </div>

      <div className="messages-area">
        {messages.length === 0 ? (
          <div className="empty-messages">
            <p>No hay mensajes aún</p>
            <p className="text-sm">Usa este chat para coordinar el servicio</p>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <ChatMessage
              key={idx}
              username={msg.username}
              message={msg.message}
              timestamp={msg.timestamp || msg.created_at}
              isOwn={msg.userId === user?.id || msg.user_id === user?.id}
              showUsername={true}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="message-input" onSubmit={handleSendMessage}>
        <input
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          placeholder="Mensaje de transporte..."
          maxLength={500}
          disabled={!user || !socket || !requestId}
        />
        <button type="submit" disabled={!user || !socket || !requestId || !inputMessage.trim()}>
          <Send size={18} />
        </button>
      </form>
    </div>
  );
};

export default TransportChatTab;
